// Server-side PDF export — POSTs self-contained HTML to aica
// (/document/pdf/render) which invokes a headless-Chromium Lambda and relays the PDF.
//
// Why: html2canvas rasterises at screen resolution, producing blurry text at
// high zoom and inflated file sizes. Headless Chrome renders the same HTML the
// browser paints, producing compact, text-selectable vector PDFs.
//
// How it works:
//   1. Clone the editor content through the shared exportHelpers pre-processing
//      pipeline: strip chrome, normalise table columns, trim empty trailing
//      blocks, wait for images. When `injectModelPageBreaks` is on (RECUR-27304
//      Delta v3), the page-boundary MODEL's page-starts are computed against
//      the LIVE content and then marked on the CLONE (never the live editor
//      DOM — see `cloneContentForExport`), mid-table starts physically split
//      the cloned table (see `splitClonedTablesAtRows`), and mid-paragraph
//      LINE starts physically split the cloned paragraph (Delta v5, see
//      `splitClonedParagraphsAtLines`), so Chromium breaks exactly where the
//      editor's own overlay does, instead of its own free pagination.
//   2. Collect the FULL host CSS (every <style> + same-origin <link>) so the PDF
//      is pixel-identical to the editor — NOT a selector-pruned subset, which
//      silently drops layout/variable rules and breaks fidelity.
//   3. Inline every @font-face as a data: URI so brand fonts render on the
//      Lambda (which blocks network during render, so fonts can't be fetched).
//   4. gzip the self-contained HTML and POST { htmlGz, fileName } to aica.
//   5. aica returns binary application/pdf; axios delivers it as a Blob.
//   6. On failure the error is captured to Sentry and RETHROWN — there is no
//      raster fallback (RECUR-26973): raster page boundaries diverged from
//      the previewed model, so a silent fallback traded an honest, fixable
//      error for a wrong document. Callers show their own failure toast.

import axios from 'axios';
import * as Sentry from '@sentry/react';
import { aicaBaseUrl } from '../../../utils/axios-interceptor';
import plateEditorStyles from '../PlateEditor.module.scss';
import { EDITOR_FONT_FAMILIES } from './exportToPdf';
import { PNO_HEADER_BAND_PX, PNO_LOGO_WIDTH_IN, PNO_LOGO_MAX_HEIGHT_IN, PNO_LOGO_GAP_BELOW_IN } from './pageGeometry';
import {
    computePageStartUnits,
    computeRowGroups,
    findTableElement,
    getSlateCellChildren,
    getTopLevelBlocks,
    measureBlockLines,
    PAGE_GAP_CSS_VAR,
    PAGE_END_GAP_CSS_VAR,
    type PageStartUnit,
} from './pageStartUnits';
import {
    waitForImages,
    inlineImagesToDataUris,
    stripInteractiveChrome,
    normalizeTableColumnsForPrint,
    trimTrailingEmptyBlocks,
    trimTrailingWhitespace,
} from './exportHelpers';
import {
    addPdfExportBreadcrumb,
    estimatePdfPageCount,
    reportPageCountDesyncIfAny,
    reportPdfServerExportFailure,
    snapshotExportPlan,
    type ExportPlanSnapshot,
    type PdfRenderRetryContext,
} from './paginationTelemetry';

// RECUR-27604 PNO page-header rework — module constants (exported for tests,
// mirrors this file's existing PAGE_GAP_CSS_VAR / MIN_PLAUSIBLE_PDF_BYTES
// precedent). The literal 'data-pdf-page-break' attribute name previously
// appeared 4 times in this file (marking a block-kind start, a split table
// continuation, a split paragraph continuation, and the CSS rule that forces
// the break) — folded onto PAGE_BREAK_ATTR everywhere it's touched by this
// change so there is exactly one place that spells the attribute name.
export const PAGE_BREAK_ATTR = 'data-pdf-page-break';
export const PAGE_WRAP_CLASS = 'pdf-page-wrap';
export const PAGE_BODY_CLASS = 'pdf-page-body';
export const LOGO_BAND_CLASS = 'pdf-pno-logo';

interface ExportToPdfServerOptions {
    target: HTMLElement;
    filename?: string;
    /** When true, return the Blob instead of triggering a download. */
    returnBlob?: boolean;
    /**
     * RECUR-27304 Delta v3: force the exported PDF to break pages EXACTLY
     * where the editor's page-boundary model (`pageBreakModel` /
     * `usePageBoundaries`) decides, instead of trusting Chromium's own native
     * pagination. Default false — every caller except the two page-gap
     * surfaces (One Pager, Reports `REPORTS`/`ONE_PAGER`) stays byte-identical.
     */
    injectModelPageBreaks?: boolean;
    /**
     * PNO logo as a self-contained data: URI. When present, it is rendered
     * top-left on EVERY page of the exported PDF — a `<thead>` band in a
     * per-model-page wrapper table (RECUR-27604; see `wrapClonePagesWithLogoHeader`),
     * which Chromium repeats on any page that wrapper fragments onto. Absent →
     * nothing is rendered and the page layout is byte-identical to before.
     * Callers resolve this via `usePnoReportLogo` (gated on the
     * `reports.logo.enabled` visibility flag); the report export surfaces pass
     * it, other exports don't.
     */
    pnoLogoDataUrl?: string;
}

// Shared DOM pre-processing helpers (waitForImages, stripInteractiveChrome,
// normalizeTableColumnsForPrint, trimTrailingEmptyBlocks, inlineImagesToDataUris)
// live in ./exportHelpers and are imported above.

// Collect the FULL host CSS: every <style> as source text plus same-origin
// <link rel=stylesheet> fetched and inlined. We deliberately do NOT prune by
// selector — selector-pruning silently drops load-bearing rules (selectors
// anchored on the editor's own container class, `:root` custom-property
// definitions, ancestor base typography) and visibly wrecks layout/fonts. The
// whole stylesheet is sent instead; it's gzipped on the wire (see gzipToBase64),
// so the payload stays small while the PDF stays pixel-identical to the editor.
//
// External stylesheets (CDNs, Google Fonts) are dropped — the Lambda can't fetch
// them and they aren't needed once @font-face is inlined (see inlineFonts).
async function collectFullCss(): Promise<string> {
    const nodes = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'));
    const parts = await Promise.all(
        nodes.map(async (n) => {
            if (n.tagName === 'LINK') {
                const href = (n as HTMLLinkElement).href || '';
                const sameOrigin = href.startsWith(window.location.origin) || href.startsWith('/');
                if (!sameOrigin) return '';
                try {
                    const css = await fetch(href).then((r) => (r.ok ? r.text() : ''));
                    return css ? `<style>${css}</style>` : '';
                } catch {
                    return '';
                }
            }
            return (n as HTMLElement).outerHTML;
        }),
    );
    return parts.filter(Boolean).join('\n');
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    const CHUNK = 0x8000; // chunk to avoid call-stack limits on large buffers
    for (let i = 0; i < bytes.length; i += CHUNK) {
        binary += String.fromCharCode(...Array.from(bytes.subarray(i, i + CHUNK)));
    }
    return btoa(binary);
}

// ── Usage-filtered font inlining (RECUR-27304 payload-size fix) ──────────────
//
// `document.styleSheets` carries EVERY @font-face declared ANYWHERE on the
// page — not just the families this document's content actually uses. A One
// Pager whose runs are all `font-family: Calibri` still pulled in every OTHER
// family declared anywhere in the app (Word-metric replacements for
// Cambria/Times New Roman/Arial, PLUS entirely unrelated families other
// modules declare, e.g. AicaReportV2's Geist/Fraunces) — 18 font files
// ≈2.5MB gzipped on a real captured payload, a major contributor to a
// media-heavy doc's export request blowing past the render Lambda's ~6MB
// sync-payload limit (see fetchPdfFromAica's non-PDF-response guard below for
// the other half of this fix). Only the @font-face rules the CLONE's content
// actually needs are embedded now.

function normalizeFontFamilyName(raw: string): string {
    return raw
        .replace(/^['"]|['"]$/g, '')
        .trim()
        .toLowerCase();
}

// Families the app must ALWAYS embed regardless of what this document's
// content happens to use: Gilroy is the brand body face (applied via CSS
// classes, not necessarily an inline `font-family` a usage scan would catch)
// and Pacifico is the EL signature script font (used by Reports). Reuses the
// raster exporter's own force-load list (exportToPdf.ts's
// EDITOR_FONT_FAMILIES) — the same two brand faces, same "the app needs this
// on every export" reason — rather than a second hardcoded copy.
const ALWAYS_EMBEDDED_FONT_FAMILIES = new Set(EDITOR_FONT_FAMILIES.map(normalizeFontFamilyName));

// Every `font-family` set as an INLINE style anywhere in `clone` (including
// the clone root itself), lowercased/unquoted, with multi-family stacks
// ("Calibri, sans-serif") split into their individual names. Reads only
// inline styles — the clone is a detached tree with no computed styles to
// fall back on, and every family this app paints onto export content (Word
// import runs, the FontFamilyPlugin's marks) is always set inline. Exported
// so this is unit-testable directly.
export function collectUsedFontFamilies(clone: HTMLElement): Set<string> {
    const families = new Set<string>();
    const addStack = (stack: string | null | undefined) => {
        (stack || '').split(',').forEach((part) => {
            const name = normalizeFontFamilyName(part);
            if (name) families.add(name);
        });
    };
    addStack(clone.style.fontFamily);
    clone.querySelectorAll<HTMLElement>('[style*="font-family"]').forEach((el) => addStack(el.style.fontFamily));
    return families;
}

// The set of font-family names (lowercased/unquoted) this export should
// embed, or `null` to mean "keep every @font-face regardless of family" — the
// safe degrade-to-oversized fallback, now used ONLY when the content scan
// genuinely FAILS (throws). An EMPTY scan is NOT a failure: a default-font
// report legitimately carries no inline font-family yet still renders in the
// brand face, so we embed the brand faces (ALWAYS_EMBEDDED_FONT_FAMILIES) and
// return that set. The old code returned `null` on an empty scan too, which
// degraded EVERY default-font doc to "embed every @font-face on the page" and
// blew the payload past the render service's ~6MB Lambda-invoke cap (413 →
// raster fallback → the editor "collapse"; a regression hole in RECUR-27304's
// own payload-reduction fix). Exported (alongside collectUsedFontFamilies) so
// both branches are directly unit-testable.
export function resolveEmbeddedFontFamilies(clone: HTMLElement): Set<string> | null {
    try {
        const used = collectUsedFontFamilies(clone);
        // Always embed the brand faces: this both covers the default-font
        // (empty-scan) case — the overwhelmingly common report — and keeps
        // brand fonts rendering even when the content only names other families.
        ALWAYS_EMBEDDED_FONT_FAMILIES.forEach((family) => used.add(family));
        return used;
    } catch {
        // A genuine scan failure is indistinguishable from "missed something",
        // so degrade to embed-all (oversized but correct) rather than dropping fonts.
        return null;
    }
}

// Inline every relevant @font-face as a data: URI so brand fonts (e.g. Gilroy)
// actually render on the Lambda. The Lambda aborts all network fetches during
// render (so an unreachable/slow font URL can never stall it), which means
// fonts MUST travel inline or text silently falls back to a system serif.
// Font files are same-origin in dev (webpack dev server) and prod (app
// origin), so the browser can fetch them here and embed the bytes.
//
// Exported so the usage-filtering behaviour is directly unit-testable,
// isolated from collectFullCss's DELIBERATELY unfiltered whole-stylesheet
// copy (buildSelfContainedHtml runs both in parallel into ONE document —
// testing this in isolation avoids a false failure from collectFullCss's
// verbatim copy of an unrelated @font-face rule's ORIGINAL (non-data-URI)
// declaration, which is unaffected by this fix by design).
export async function inlineFonts(clone: HTMLElement): Promise<string> {
    const embeddedFamilies = resolveEmbeddedFontFamilies(clone);

    const faceRules: CSSFontFaceRule[] = [];
    for (const sheet of Array.from(document.styleSheets)) {
        let rules: CSSRuleList | null = null;
        try {
            rules = sheet.cssRules;
        } catch {
            continue; // cross-origin sheet — not readable
        }
        if (!rules) continue;
        for (const rule of Array.from(rules)) {
            if (rule instanceof CSSFontFaceRule) faceRules.push(rule);
        }
    }

    // `null` means the usage scan couldn't be trusted (threw, or found
    // nothing) — keep every face exactly as before rather than risk dropping
    // a glyph the document actually needs.
    const relevantFaceRules = embeddedFamilies
        ? faceRules.filter((rule) => embeddedFamilies.has(normalizeFontFamilyName(rule.style.getPropertyValue('font-family'))))
        : faceRules;

    const resolveUrl = (raw: string): string => {
        if (raw.startsWith('http') || raw.startsWith('data:')) return raw;
        if (raw.startsWith('//')) return window.location.protocol + raw;
        return new URL(raw, window.location.href).href;
    };

    const faces = await Promise.all(
        relevantFaceRules.map(async (rule) => {
            const src = rule.style.getPropertyValue('src');
            const match = src.match(/url\(\s*["']?([^)"']+\.(?:woff2|woff|ttf|otf))["']?\s*\)/i);
            if (!match) return '';
            const family = rule.style.getPropertyValue('font-family');
            const weight = rule.style.getPropertyValue('font-weight') || 'normal';
            const fontStyle = rule.style.getPropertyValue('font-style') || 'normal';
            try {
                const buf = await fetch(resolveUrl(match[1])).then((r) => (r.ok ? r.arrayBuffer() : null));
                if (!buf) return '';
                const ext = match[1].split(/[?#]/)[0].split('.').pop()!.toLowerCase();
                const mime = ext === 'woff2' ? 'font/woff2' : ext === 'woff' ? 'font/woff' : ext === 'otf' ? 'font/otf' : 'font/ttf';
                const fmt = ext === 'woff2' ? 'woff2' : ext === 'woff' ? 'woff' : ext === 'otf' ? 'opentype' : 'truetype';
                return `@font-face{font-family:${family};font-style:${fontStyle};font-weight:${weight};font-display:swap;src:url(data:${mime};base64,${arrayBufferToBase64(
                    buf,
                )}) format("${fmt}")}`;
            } catch {
                return '';
            }
        }),
    );

    const block = faces.filter(Boolean).join('\n');
    return block ? `<style>${block}</style>` : '';
}

// gzip the payload in the browser so the full stylesheet + inlined fonts fit well
// under the Lambda Function URL's 6 MB request limit (≈5.7 MB raw → ≈1.7 MB).
// CompressionStream ships in all Chromium-based browsers, which this app targets.
async function gzipToBase64(text: string): Promise<string> {
    const CompressionStreamCtor = (window as unknown as { CompressionStream?: any }).CompressionStream;
    // `pipeThrough` isn't in this TS lib's ReadableStream type but exists at
    // runtime in all Chromium browsers — cast through `any`.
    const blobStream = new Blob([text]).stream() as any;
    const stream = blobStream.pipeThrough(new CompressionStreamCtor('gzip'));
    const buffer = await new Response(stream).arrayBuffer();
    return arrayBufferToBase64(buffer);
}

// ── Deterministic page breaks (RECUR-27304 Delta v3) ──────────────────────────
//
// Force Chromium to break EXACTLY where the editor's page-boundary model
// decided, instead of trusting its own native pagination (which free-
// fragments text/table rows under the RECUR-26249 break-inside:auto override
// and can land a heading mid-page — see the plan's Delta v3 section for why
// 3 overlay-side prediction attempts failed before this).

// Strips the live gap-display styling (RECUR-27304 gap-display) from `root`
// and every descendant. `usePageBoundaries`'s `applyPageGapStyles` paints a
// real `margin-top` on live page-start blocks (via the `--page-gap` custom
// property + `.pageGapBefore` class, see PlateEditor.module.scss) purely so
// the EDITOR can show the same blank space the PDF leaves behind — the PDF
// itself gets that space entirely from its own forced `data-pdf-page-break`
// breaks. `cloneNode` copies inline styles/classes verbatim, so left alone
// the clone would carry the same margin ON TOP of the forced break it lands
// next to, double-counting the gap into stranded headings and near-empty
// pages. Only ever called on the detached CLONE — never `contentEl` itself,
// which `usePageBoundaries` owns exclusively.
function stripPageGapStyling(root: HTMLElement): void {
    // Removing the inline var alone already zeroes the margin (the SCSS rule
    // falls back to 0px), but strip both for hygiene.
    const gapVarElements = [root, ...Array.from(root.querySelectorAll<HTMLElement>(`[style*="${PAGE_GAP_CSS_VAR}"]`))];
    gapVarElements.forEach((el) => el.style.removeProperty(PAGE_GAP_CSS_VAR));

    // Exact class-name lookup only — production CSS-module hashes don't
    // preserve the local `pageGapBefore` name, so a substring match
    // (`[class*="pageGapBefore"]`) would silently miss the real hashed class.
    // `plateEditorStyles.pageGapBefore` always resolves to whatever the hook
    // itself painted, so this stays correct in both prod and tests.
    const gapClass = plateEditorStyles.pageGapBefore;
    const gapClassElements = [root, ...Array.from(root.getElementsByClassName(gapClass))] as HTMLElement[];
    gapClassElements.forEach((el) => el.classList.remove(gapClass));

    // RECUR-27604 §13.3(b) — the page-END mirror. `PAGE_END_GAP_CSS_VAR`
    // (`--page-end-gap`) is a DELIBERATELY separate property name from
    // `PAGE_GAP_CSS_VAR` (`--page-gap`, see that constant's own doc) — the
    // `[style*="--page-gap"]` selector above does NOT match it (no substring
    // overlap: "...page-END-gap" vs "...page-gap"), so it needs its own strip
    // pass, covering every element (block AND row) via `querySelectorAll`
    // exactly like the top-gap pass above.
    const endGapVarElements = [root, ...Array.from(root.querySelectorAll<HTMLElement>(`[style*="${PAGE_END_GAP_CSS_VAR}"]`))];
    endGapVarElements.forEach((el) => el.style.removeProperty(PAGE_END_GAP_CSS_VAR));

    // Only the BLOCK-level end class is stripped here, mirroring `gapClass`
    // above exactly — the row-level classes (`pageRowGapBefore`,
    // `pageRowEndGapAfter`) are deliberately left as INERT leftovers, same
    // precedent as §5.1/§8.7: with the inline var gone, `var(--page-gap,
    // 0px)`/`var(--page-end-gap, 0px)` both fall back to 0, so a stale row
    // class resolves to base padding/height — byte-identical to a row that
    // was never gapped. Stripping them would be extra work with no
    // behavioural difference.
    const endGapClass = plateEditorStyles.pageEndGapAfter;
    const endGapClassElements = [root, ...Array.from(root.getElementsByClassName(endGapClass))] as HTMLElement[];
    endGapClassElements.forEach((el) => el.classList.remove(endGapClass));
}

// Computes whole-block page-starts against the LIVE `contentEl` (read-only —
// `computePageStartUnits` needs real layout geometry, which only an attached,
// rendered element has), clones the tree, then marks the CLONE's
// corresponding blocks with a `data-pdf-page-break` attribute. The live
// editable DOM is NEVER mutated: there is no mutation window to reason about,
// no reliance on a synchronous clone surviving Slate/slate-react's own DOM
// reconciliation (restoreDOM / MutationObserver) across a Plate upgrade, and
// so HARD REQ 1 (the live editor DOM must never carry export-only markers)
// holds by construction rather than by a try/finally cleanup.
//
// `blockIndex` (each unit's position in `getTopLevelBlocks(contentEl)`) is
// what makes this possible without touching the live element: `cloneNode`
// preserves block order 1:1, so `getTopLevelBlocks(clone)[blockIndex]` is
// always the SAME block `getTopLevelBlocks(contentEl)[blockIndex]` describes.
// Exported standalone (mirrors the exportHelpers.ts precedent of
// independently-testable DOM-mutation steps) so the no-live-DOM-mutation
// guarantee is unit-testable without the network/font/image side of
// `buildSelfContainedHtml`.
//
// Row-kind (mid-table), line-kind (mid-paragraph, RECUR-27304 Delta v5), and
// rowSegment-kind (mid-row, RECUR-27604 §14 in-row split) page-starts are
// returned alongside the clone, NOT marked here — `splitClonedTablesAtRows` /
// `splitClonedParagraphsAtLines` handle those entirely on the detached clone
// (see buildSelfContainedHtml for the order the two run in and why).
//
// `reservedPageTopPx` (RECUR-27604, default 0 — every pre-existing caller
// stays byte-identical) is forwarded straight to `computePageStartUnits` so
// the model reserves the SAME per-page logo-header space the export's own
// wrapper/CSS reserve — see pageStartUnits.ts's module doc for why this is a
// page-CAPACITY subtraction, never an offsetTop addition.
//
// `capacityScale` (RECUR-27726 self-correcting retry, default 1): forwarded
// to `computePageStartUnits` to tighten the page-height the model plans
// against for a single retried export — see that function's own doc comment.
export function cloneContentForExport(
    contentEl: HTMLElement,
    injectModelPageBreaks: boolean,
    reservedPageTopPx = 0,
    capacityScale = 1,
): {
    clone: HTMLElement;
    rowPageStarts: PageStartUnit[];
    linePageStarts: PageStartUnit[];
    rowSegmentPageStarts: PageStartUnit[];
} {
    const pageStartUnits = injectModelPageBreaks ? computePageStartUnits(contentEl, reservedPageTopPx, capacityScale) : [];
    const blockPageStarts = pageStartUnits.filter((unit) => unit.kind === 'block');
    const rowPageStarts = pageStartUnits.filter((unit) => unit.kind === 'row');
    const linePageStarts = pageStartUnits.filter((unit) => unit.kind === 'line');
    // RECUR-27604 §14 — a mid-row cut is a physical table split too (like
    // `rowPageStarts`), but the model's own "un-hostable, no display slot"
    // decision (§14.0) is the SAME reason it's kept in its own bucket rather
    // than folded into `rowPageStarts`: `splitClonedTablesAtRows` treats the
    // two differently (an ordinary row boundary vs an in-row child cut).
    const rowSegmentPageStarts = pageStartUnits.filter((unit) => unit.kind === 'rowSegment');

    const clone = contentEl.cloneNode(true) as HTMLElement;
    // RECUR-27304: editor-only gap styling must never reach the export — the
    // PDF's page gaps come entirely from its OWN forced breaks (the
    // `data-pdf-page-break` markers below); a leaked live-editor margin would
    // double-count that space on top of them, producing stranded headings and
    // near-empty pages. `cloneNode` copies the live DOM's inline `--page-gap`
    // + `.pageGapBefore` 1:1, so it must be stripped from the CLONE here —
    // never the live `contentEl`, which `usePageBoundaries` owns exclusively.
    stripPageGapStyling(clone);

    if (blockPageStarts.length > 0) {
        const cloneBlocks = getTopLevelBlocks(clone);
        blockPageStarts.forEach((unit) => {
            const cloneBlock = unit.blockIndex != null ? cloneBlocks[unit.blockIndex] : undefined;
            cloneBlock?.setAttribute(PAGE_BREAK_ATTR, '');
        });
    }

    return { clone, rowPageStarts, linePageStarts, rowSegmentPageStarts };
}

// RECUR-27304 rowspan-safe pagination — defensive floor: the page-break
// MODEL (pageStartUnits.ts's buildAtomicUnits/buildUnitMeta) only ever emits
// GROUP-boundary row indices post-fix, so this should be a no-op on every
// real call. Kept anyway so a stale/corrupt caller can never physically slice
// a rowspan span in two — snapping every requested break down to the START
// of whichever row GROUP contains it (see `computeRowGroups`) guarantees a
// spanning cell's own `<tr>` (and every `<tr>` it visually covers) always
// stays together on ONE side of the split, never straddling both. A break
// that snaps to a group's OWN start is already valid and passes through
// unchanged; duplicate snapped values collapse via the caller's existing
// `Set` de-dup.
function snapRowBreaksToGroupBoundaries(table: HTMLTableElement, rowBreaks: number[]): number[] {
    const groupStartByRow = new Map<number, number>();
    computeRowGroups(table.rows).forEach((group) => {
        for (let row = group.start; row <= group.end; row++) groupStartByRow.set(row, group.start);
    });
    return rowBreaks.map((k) => groupStartByRow.get(k) ?? k);
}

// Splits ONE cloned table at each row index in `rowBreaks` (deduped, sorted,
// each >0 and <total row count — row 0 can never be a break, it's always
// glued to the group that opens the table's own page as a 'block'-kind
// start). Rows [0..k1-1] stay in `table`; every later segment moves into a
// NEW sibling continuation `<table>` that shallow-clones the original's own
// tag/class/style/width (`cloneNode(false)`), copies the already-finalized
// `<colgroup>`, reproduces the `.tableScroll` wrapper when present, and
// carries `data-pdf-page-break` on its top-level box (the wrapper if
// present, else the table itself) — a block-level marker Chromium honours
// reliably, unlike an unverified `<tr>` `break-before`. There is no
// `<thead>` to repeat (verified — `TableElement` never renders one), so a
// continuation simply starts at its first moved row.
function splitOneTableAtRows(table: HTMLTableElement, rowBreaks: number[]): void {
    const rows = Array.from(table.rows);
    // RECUR-27304 rowspan-safe pagination: snap every break to its enclosing
    // row GROUP's own start BEFORE the existing dedup/sort/range-filter below
    // — see snapRowBreaksToGroupBoundaries' own doc comment for why this is a
    // defensive no-op today, not a behaviour change for any plain table.
    const breaks = Array.from(new Set(snapRowBreaksToGroupBoundaries(table, rowBreaks)))
        .filter((k) => k > 0 && k < rows.length)
        .sort((a, b) => a - b);
    if (breaks.length === 0) return;

    const parentWrapper = table.parentElement;
    const wrapper = parentWrapper?.matches('[class*="tableScroll"]') ? parentWrapper : null;
    const colgroup = table.querySelector('colgroup');
    const segmentEnds = [...breaks, rows.length];

    let previousBox: HTMLElement = wrapper ?? table;

    breaks.forEach((start, i) => {
        const end = segmentEnds[i + 1];
        const continuationTable = table.cloneNode(false) as HTMLTableElement; // shallow: tag/class/style/width only
        if (colgroup) continuationTable.appendChild(colgroup.cloneNode(true));
        const tbody = document.createElement('tbody');
        rows.slice(start, end).forEach((row) => tbody.appendChild(row)); // moves the row out of `table`
        continuationTable.appendChild(tbody);

        const continuationBox: HTMLElement = wrapper ? (wrapper.cloneNode(false) as HTMLElement) : continuationTable;
        if (wrapper) continuationBox.appendChild(continuationTable);
        continuationBox.setAttribute(PAGE_BREAK_ATTR, '');

        previousBox.after(continuationBox);
        previousBox = continuationBox;
    });
}

// RECUR-27604 §14 in-row split: physically cuts ONE cloned `<tr>` into two
// sibling rows at a cell-paragraph boundary planned by the model (a
// 'rowSegment' page-start). The PRIMARY cell's Slate-block children from
// `cellChildIndex` on move into the new row's matching cell; every OTHER
// cell's children partition by their LIVE counterpart's rendered bottom vs
// the cut position (measured fresh off the untouched live row — the clone's
// geometry is meaningless while detached): anything not provably above the
// cut moves DOWN, so the page being left behind can never end up taller than
// the model planned (underfilling is safe; overfilling recreates the exact
// unplanned-Chromium-cut desync this feature removes). `cellChildIndex`
// counts Slate-block children only (`getSlateCellChildren`) on BOTH the live
// row and the (chrome-stripped, per `stripInteractiveChrome`) cloned row, so
// a raw child index can never desync between them. Returns the new row,
// inserted immediately after `clonedRow` — the caller turns the boundary
// between the two into an ordinary row-boundary table slice.
//
// Known v1 limit (RECUR-27604, mirrors the reference PR's own finding): a
// straddler from a SECOND tall cell can make the continuation row taller
// than planned. Import-shaped oversized rows — one short label cell + one
// tall content cell, exactly the SEDNA shape this targets — never hit it.
// Strips a carried, fixed row height (`tableElements.tsx:162`'s persisted
// `rowSize`/resize-override `style.height` on the `<tr>` — cells themselves
// are never given an inline height/min-height, see that file's "INTENTIONAL"
// comment) from a row and its cells. `cloneNode(false)` copies inline styles
// verbatim, so BOTH fragments a §14 in-row split produces would otherwise
// keep the ORIGINAL row's full height while holding only a fraction of its
// content — the continuation fragment then renders as a near-empty box the
// height of the whole original row. Each split fragment must size to its
// OWN content instead, so this runs on both fragments right after the cut.
// `height` (the legacy attribute form, pre-dating the `style.height` path)
// is stripped defensively even though this codebase only ever writes the
// style form.
function stripCarriedRowHeight(row: HTMLTableRowElement): void {
    row.style.removeProperty('height');
    row.style.removeProperty('min-height');
    row.removeAttribute('height');
    Array.from(row.cells).forEach((cell) => {
        cell.style.removeProperty('height');
        cell.style.removeProperty('min-height');
        cell.removeAttribute('height');
    });
}

function splitOneClonedRowAtChild(
    liveRow: HTMLTableRowElement,
    clonedRow: HTMLTableRowElement,
    cellIndex: number,
    cellChildIndex: number,
): HTMLTableRowElement {
    const liveRowTop = liveRow.getBoundingClientRect().top;
    const livePrimaryChildren = liveRow.cells[cellIndex] ? getSlateCellChildren(liveRow.cells[cellIndex]) : [];
    const primaryLiveChild = livePrimaryChildren[cellChildIndex];
    const cutYWithinRow = primaryLiveChild ? primaryLiveChild.getBoundingClientRect().top - liveRowTop : 0;

    const continuationRow = clonedRow.cloneNode(false) as HTMLTableRowElement;
    Array.from(clonedRow.cells).forEach((cell, ci) => {
        const continuationCell = cell.cloneNode(false) as HTMLTableCellElement;
        continuationRow.appendChild(continuationCell);
        const cellChildren = getSlateCellChildren(cell);
        if (ci === cellIndex) {
            cellChildren.slice(cellChildIndex).forEach((child) => continuationCell.appendChild(child));
            return;
        }
        const liveChildren = liveRow.cells[ci] ? getSlateCellChildren(liveRow.cells[ci]) : [];
        cellChildren.forEach((child, chIdx) => {
            const liveChild = liveChildren[chIdx];
            const bottomWithinRow = liveChild ? liveChild.getBoundingClientRect().bottom - liveRowTop : 0;
            if (bottomWithinRow > cutYWithinRow) continuationCell.appendChild(child);
        });
    });
    // Both fragments now hold only a slice of the original row's content —
    // neither should keep the original's full-row height (see
    // `stripCarriedRowHeight` above).
    stripCarriedRowHeight(clonedRow);
    stripCarriedRowHeight(continuationRow);
    clonedRow.after(continuationRow);
    return continuationRow;
}

// Physically splits the CLONED tables at the model's row-level page-starts —
// and, first, cuts any oversized row at its 'rowSegment' page-starts
// (RECUR-27604 §14 in-row split), turning each segment boundary into an
// ordinary row boundary the slicing below handles identically.
// Runs ONLY on the detached `clone`, AFTER `normalizeTableColumnsForPrint` (so
// each live<->clone table pairing is still 1:1 by document order — the SAME
// pairing `normalizeTableColumnsForPrint` itself relies on — and every
// continuation copies the already-finalized `<colgroup>`). The live editor /
// `editor.children` are never touched by this step (HARD REQ 1 intact for the
// row path too). Exported standalone (mirrors the exportHelpers.ts precedent)
// so the split is unit-testable without the network/font/image side of
// `buildSelfContainedHtml`.
export function splitClonedTablesAtRows(
    clone: HTMLElement,
    liveContent: HTMLElement,
    rowPageStarts: PageStartUnit[],
    rowSegmentPageStarts: PageStartUnit[] = [],
): void {
    const liveTables = Array.from(liveContent.querySelectorAll('table'));
    const cloneTables = Array.from(clone.querySelectorAll('table'));
    const tableIndexOf = (unit: PageStartUnit): number => {
        const liveTable = unit.tableBlock ? findTableElement(unit.tableBlock) : null;
        return liveTable ? liveTables.indexOf(liveTable) : -1;
    };

    // Validated once here (RECUR-27604 §14) — every later use reads the
    // narrowed fields without repeated `as number` casts.
    type SegmentStart = PageStartUnit & { rowIndex: number; cellIndex: number; cellChildIndex: number };

    const rowStartsByTableIndex = new Map<number, PageStartUnit[]>();
    rowPageStarts.forEach((unit) => {
        const tableIndex = tableIndexOf(unit);
        if (tableIndex === -1 || unit.rowIndex == null) return;
        const units = rowStartsByTableIndex.get(tableIndex) ?? [];
        units.push(unit);
        rowStartsByTableIndex.set(tableIndex, units);
    });
    const segmentStartsByTableIndex = new Map<number, SegmentStart[]>();
    rowSegmentPageStarts.forEach((unit) => {
        const tableIndex = tableIndexOf(unit);
        if (tableIndex === -1 || unit.rowIndex == null || unit.cellIndex == null || unit.cellChildIndex == null) return;
        const units = segmentStartsByTableIndex.get(tableIndex) ?? [];
        units.push(unit as SegmentStart);
        segmentStartsByTableIndex.set(tableIndex, units);
    });

    new Set([...rowStartsByTableIndex.keys(), ...segmentStartsByTableIndex.keys()]).forEach((tableIndex) => {
        const clonedTable = cloneTables[tableIndex];
        const liveTable = liveTables[tableIndex];
        if (!clonedTable || !liveTable) return;
        // Snapshot BEFORE any in-row cut: aligns 1:1 with the live table's
        // rows, so a live rowIndex resolves to its cloned <tr> by position.
        const clonedRowsBefore = Array.from(clonedTable.rows);

        // In-row cuts first, per row in DESCENDING child order: each cut
        // moves a suffix out, so earlier cut points stay valid; each
        // continuation inserts right after the (shrinking) original row, so
        // multiple continuations end up in the correct order.
        const breakRows: HTMLTableRowElement[] = [];
        const segmentsByRow = new Map<number, SegmentStart[]>();
        (segmentStartsByTableIndex.get(tableIndex) ?? []).forEach((unit) => {
            const units = segmentsByRow.get(unit.rowIndex) ?? [];
            units.push(unit);
            segmentsByRow.set(unit.rowIndex, units);
        });
        segmentsByRow.forEach((units, rowIndex) => {
            const liveRow = liveTable.rows[rowIndex];
            const clonedRow = clonedRowsBefore[rowIndex];
            if (!liveRow || !clonedRow) return;
            [...units]
                .sort((a, b) => b.cellChildIndex - a.cellChildIndex)
                .forEach((unit) => {
                    breakRows.push(splitOneClonedRowAtChild(liveRow, clonedRow, unit.cellIndex, unit.cellChildIndex));
                });
        });

        // Row-boundary breaks resolve by ELEMENT position in the (possibly
        // cut-expanded) cloned table — index arithmetic would drift by one
        // per inserted continuation row.
        const finalRows = Array.from(clonedTable.rows);
        const rowBreaks: number[] = [];
        (rowStartsByTableIndex.get(tableIndex) ?? []).forEach((unit) => {
            const index = finalRows.indexOf(clonedRowsBefore[unit.rowIndex as number]);
            if (index > 0) rowBreaks.push(index);
        });
        breakRows.forEach((row) => {
            const index = finalRows.indexOf(row);
            if (index > 0) rowBreaks.push(index);
        });
        if (rowBreaks.length > 0) splitOneTableAtRows(clonedTable, rowBreaks);
    });
}

// ── Paragraph line-splitting (RECUR-27304 Delta v5) ───────────────────────────
//
// A long TEXT paragraph splits BETWEEN rendered lines like Word, instead of
// being pushed whole to the next page (which left a page-sized gap). Mirrors
// splitClonedTablesAtRows' shape/pairing approach (live<->clone paired by
// TOP-LEVEL BLOCK INDEX here, rather than by table identity — see
// `splitClonedParagraphsAtLines` below), but the SPLIT POINT itself can't be
// read off the model's own `offsetTop` (NORMALIZED/gap-free — see
// pageStartUnits.ts's coordinate-space note): a gap painted on this exact
// block by the live editor overlay would put that value and a freshly
// measured caret rect in two DIFFERENT coordinate spaces. Instead, the split
// point is found by RE-MEASURING the live paragraph's lines fresh (same
// `measureBlockLines` the model itself uses) and walking its text nodes for
// the caret position that reaches the target line — both measurements happen
// back-to-back against the SAME (untouched-throughout-export) live DOM, so
// they can never disagree.

/** A resolved split position: a child-index path from a block's root down to a text node, plus a character offset within it. */
interface SplitPoint {
    path: number[];
    offset: number;
}

/** Child-index path from `root` down to `node` (e.g. `[0, 2, 0]`). `[]` if `node` isn't a descendant of `root`. */
function pathFromBlockRoot(root: HTMLElement, node: Node): number[] {
    const path: number[] = [];
    let current: Node = node;
    while (current !== root) {
        const parent = current.parentNode;
        if (!parent) return [];
        path.unshift(Array.prototype.indexOf.call(parent.childNodes, current));
        current = parent;
    }
    return path;
}

/** Resolves a `pathFromBlockRoot` path against a (structurally identical) `root` — used to find the CLONE's corresponding node from a path recorded against the LIVE one. `null` if the path doesn't resolve. */
function resolvePath(root: HTMLElement, path: number[]): Node | null {
    let node: Node = root;
    for (const idx of path) {
        const child: ChildNode | undefined = node.childNodes[idx];
        if (!child) return null;
        node = child;
    }
    return node;
}

function collectTextNodes(root: HTMLElement): Text[] {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const nodes: Text[] = [];
    let node = walker.nextNode();
    while (node) {
        if ((node.textContent ?? '').length > 0) nodes.push(node as Text);
        node = walker.nextNode();
    }
    return nodes;
}

const CARET_TOP_TOLERANCE_PX = 2;

/**
 * Finds the character position where `targetLineTop` (a VIEWPORT Y, from
 * `measureBlockLines`) begins WITHIN `block`'s own text: walks every text
 * node in document order, skipping any whose LAST character never reaches
 * the target (the whole node is on an earlier line), then binary-searches
 * the first node that does reach it for the smallest offset whose caret
 * rect (`Range.getClientRects()` for a single character) has reached the
 * target line's top. `null` when no caret position reaches the target
 * within tolerance — measurement failure; the caller leaves that split
 * point unresolved and the segment falls back to today's whole-block PUSH
 * behaviour (never throws).
 */
function findSplitPoint(block: HTMLElement, targetLineTop: number): SplitPoint | null {
    const textNodes = collectTextNodes(block);
    if (textNodes.length === 0) return null;
    const range = document.createRange();
    const reaches = (node: Text, offset: number): boolean => {
        const length = node.textContent?.length ?? 0;
        range.setStart(node, offset);
        range.setEnd(node, Math.min(offset + 1, length));
        const rect = range.getClientRects()[0];
        return !!rect && rect.top >= targetLineTop - CARET_TOP_TOLERANCE_PX;
    };

    for (const textNode of textNodes) {
        const length = textNode.textContent?.length ?? 0;
        if (length === 0 || !reaches(textNode, length - 1)) continue; // whole node still on an earlier line — try the next one

        let lo = 0;
        let hi = length - 1;
        while (lo < hi) {
            const mid = (lo + hi) >>> 1;
            if (reaches(textNode, mid)) hi = mid;
            else lo = mid + 1;
        }
        return { path: pathFromBlockRoot(block, textNode), offset: lo };
    }
    return null;
}

// Matches `ol > li[data-align]::before { min-width: 20px }` (PlateEditor.module.scss)
// — see suppressListMarkerOnContinuation's doc comment for why this is the ONE
// marker mode that needs compensating.
const LIST_MARKER_INLINE_GUTTER_PX = 20;

/**
 * Suppresses the list bullet/number on a SPLIT-OFF paragraph continuation
 * (RECUR-27304 Delta v5) — Word never repeats a list marker on a wrapped
 * continuation line. `range.extractContents()` (in `splitOneParagraphAtOffset`
 * below) reconstructs a fresh `<ul>/<ol><li>` around the extracted tail when
 * the split point falls inside a list item's BlockList wrapper (see
 * `BlockList.tsx`) — carrying the SAME inline `counterReset` /
 * `listStyleType`, which would otherwise repaint the SAME bullet/number.
 *
 * Every marker this app paints — the native `::marker` (disc/circle), the
 * painted square glyph, and the painted ordinal digit — is keyed off this
 * EXACT inline `list-style-type` VALUE via an attribute-substring CSS
 * selector (see PlateEditor.module.scss's `ul[style*='list-style-type:
 * square']`, `ol[style*='list-style-type: decimal']`, etc.). Overwriting the
 * value to `none` breaks every one of those selector matches in one step —
 * no competing `!important` rule to fight, since we're changing the STRING
 * the selectors themselves match against, not fighting cascade specificity —
 * and also stops the shared `plate-ordered-list` counter-increment from
 * firing (its `content` is only ever set by those same conditional rules),
 * so no sibling item's numbering is perturbed by this continuation's own copy.
 *
 * Indent: every marker mode above renders OUTSIDE the `<li>`'s own content
 * box (native `list-style-position: outside`, or `position: absolute` for
 * the painted glyphs), so removing it shifts nothing. The ONE exception is a
 * data-align'd (non-left) ORDERED item, whose digit becomes `display:
 * inline-block; min-width: 20px` sharing the (inline) text's own line box
 * (`ol > li[data-align]::before`) — padding that one case back in so the
 * continuation's text doesn't visibly jump left.
 */
function suppressListMarkerOnContinuation(continuationBlock: HTMLElement): void {
    const list = continuationBlock.querySelector<HTMLElement>('ul, ol');
    if (!list) return; // not a list item — nothing to suppress
    list.style.listStyleType = 'none';
    const li = list.querySelector<HTMLElement>('li');
    if (li?.hasAttribute('data-align') && list.tagName === 'OL') {
        li.style.setProperty('padding-left', `${LIST_MARKER_INLINE_GUTTER_PX}px`, 'important');
    }
}

// Splits `clonedBlock` at `(splitNode, splitOffset)`: a shallow clone
// (`cloneNode(false)`, keeping tag/classes/inline styles) becomes the
// continuation, `Range.extractContents()` moves everything from the split
// point to the end of `clonedBlock`'s CURRENT content into it (reconstructing
// any partial ancestor — e.g. a list item's `<ul>/<li>` — as needed), and the
// continuation is inserted immediately after `clonedBlock` carrying
// `data-pdf-page-break`. Inline marks (bold/italic/links) spanning the split
// are preserved by `extractContents` (it clones the partial wrappers). A
// continuation's first-line `text-indent` is zeroed (Word never repeats it on
// a wrapped continuation) and any list marker is suppressed (see
// `suppressListMarkerOnContinuation`).
function splitOneParagraphAtOffset(clonedBlock: HTMLElement, splitNode: Node, splitOffset: number): void {
    const continuation = clonedBlock.cloneNode(false) as HTMLElement;
    const range = document.createRange();
    range.setStart(splitNode, splitOffset);
    range.setEnd(clonedBlock, clonedBlock.childNodes.length);
    continuation.appendChild(range.extractContents());
    continuation.setAttribute(PAGE_BREAK_ATTR, '');
    continuation.style.textIndent = '0';
    suppressListMarkerOnContinuation(continuation);
    clonedBlock.after(continuation);
}

/**
 * Physically splits CLONED paragraphs at the model's LINE-level page-starts
 * (RECUR-27304 Delta v5) — the paragraph analogue of `splitClonedTablesAtRows`.
 * Must run BEFORE `splitClonedTablesAtRows` (see `buildSelfContainedHtml`'s
 * call site) — this function's live<->clone pairing is by TOP-LEVEL BLOCK
 * INDEX (`getTopLevelBlocks`), which table-splitting's OWN insertion of new
 * sibling `<table>`s would shift; table-splitting's pairing (by
 * `querySelectorAll('table')`, unaffected by extra `<p>` siblings anywhere)
 * is safe to run either way, so the ordering constraint is one-directional.
 *
 * One paragraph can have MULTIPLE line-starts (spans 3+ pages) — grouped by
 * `textBlock`, every split point is RESOLVED against the still-pristine clone
 * block FIRST (a captured Node reference stays valid even after an unrelated
 * LATER part of the same tree is extracted), then applied in DESCENDING
 * `lineIndex` order: each split extracts `[splitPoint .. end of the clone
 * block's CURRENT content]` into a fresh continuation inserted right after
 * it, so processing the highest line first means every earlier (lower-index,
 * still-to-process) node reference was never part of an already-extracted
 * region.
 *
 * Never throws: a unit whose split point can't be resolved (measurement
 * failure — see `findSplitPoint`) is simply skipped, leaving that portion of
 * the paragraph as today's whole-block PUSH behaviour (the fallback safety
 * net RECUR-27304 Delta v5 requires).
 */
export function splitClonedParagraphsAtLines(clone: HTMLElement, liveContent: HTMLElement, linePageStarts: PageStartUnit[]): void {
    if (linePageStarts.length === 0) return;
    const liveBlocks = getTopLevelBlocks(liveContent);
    const cloneBlocks = getTopLevelBlocks(clone);

    const unitsByTextBlock = new Map<HTMLElement, PageStartUnit[]>();
    linePageStarts.forEach((unit) => {
        if (!unit.textBlock || unit.lineIndex == null) return;
        const units = unitsByTextBlock.get(unit.textBlock) ?? [];
        units.push(unit);
        unitsByTextBlock.set(unit.textBlock, units);
    });

    unitsByTextBlock.forEach((units, textBlock) => {
        const blockIndex = liveBlocks.indexOf(textBlock);
        const cloneBlock = blockIndex === -1 ? undefined : cloneBlocks[blockIndex];
        if (!cloneBlock) return;

        // Re-measure the LIVE block's lines fresh, in the SAME (viewport)
        // space findSplitPoint's caret-rect comparisons use — see this
        // section's own module comment for why the model's own offsetTop
        // isn't reused here.
        const liveLines = measureBlockLines(textBlock);

        const resolved = units
            .map((unit) => {
                const targetLineTop = unit.lineIndex != null ? liveLines[unit.lineIndex]?.top : undefined;
                if (targetLineTop == null) return null;
                const splitPoint = findSplitPoint(textBlock, targetLineTop);
                if (!splitPoint) return null;
                const node = resolvePath(cloneBlock, splitPoint.path);
                // `resolvePath` resolves an EMPTY path to `cloneBlock` itself
                // (not a descendant) rather than failing — `splitOffset` is a
                // CHARACTER offset, only meaningful against a Text node;
                // `splitOneParagraphAtOffset` would otherwise call
                // `range.setStart(someElement, charOffset)`, and an
                // out-of-range child-node index throws IndexSizeError and
                // aborts the whole export. Unreachable today (`findSplitPoint`
                // always derives `path` from an actual text-node descendant
                // via `collectTextNodes`), but this keeps the "never throws"
                // guarantee structural rather than incidental — treat
                // anything but a resolved Text node as unresolved, same as a
                // `null` splitPoint above (existing whole-block PUSH fallback).
                return node instanceof Text ? { lineIndex: unit.lineIndex as number, node, offset: splitPoint.offset } : null;
            })
            .filter((r): r is { lineIndex: number; node: Text; offset: number } => r !== null)
            .sort((a, b) => b.lineIndex - a.lineIndex); // descending — see this function's own doc comment

        resolved.forEach(({ node, offset }) => splitOneParagraphAtOffset(cloneBlock, node, offset));
    });
}

// ── PNO page header (RECUR-27604) ─────────────────────────────────────────────
//
// Wraps every model PAGE of the clone's top-level children into its own
// `<table class="pdf-page-wrap">`, logo in the `<thead>` (Chromium repeats a
// `<thead>` on any page the table fragments onto — the safety net for a
// drift-created page the model never predicted), page content in a single
// `<tbody><tr><td><div class="pdf-page-body">`. Candidate A of the plan's
// technique decision (§1): every forced `break-before: page` still lands on a
// TOP-LEVEL block box — the wrapper table itself — the exact mechanism this
// file's own `[data-pdf-page-break]` CSS rule already proves, rather than
// depending on an unverified break-before-inside-a-cell construct.

/** One run of the clone's original top-level children that becomes one wrapper's page body. */
function groupClonePagesForLogoHeader(children: ChildNode[]): ChildNode[][] {
    // Run boundaries: index 0 always opens the first run; every top-level
    // element carrying PAGE_BREAK_ATTR opens another (mirrors the CSS
    // `break-before: page` semantics this same attribute already drives —
    // one run per model page). When a marker happens to sit on `children[0]`
    // itself (defensive — a page-start marker is never expected on the very
    // first top-level node in practice), BOTH rules fire at the SAME index,
    // producing two adjacent boundaries and therefore one EMPTY run between
    // them — filtered out below (step 5, the empty-run guard) rather than
    // assumed never to happen.
    const markerIndices = children.reduce<number[]>((acc, node, index) => {
        if (node instanceof HTMLElement && node.hasAttribute(PAGE_BREAK_ATTR)) acc.push(index);
        return acc;
    }, []);
    const boundaries = [0, ...markerIndices];
    return boundaries.map((start, i) => children.slice(start, boundaries[i + 1] ?? children.length));
}

/** Builds one detached `<table class="pdf-page-wrap">` for `pageChildren`, moving them (not copying) into its `.pdf-page-body` holder. */
function buildLogoPageWrapper(pageChildren: ChildNode[], logoDataUrl: string): HTMLTableElement {
    const wrapper = document.createElement('table');
    wrapper.className = PAGE_WRAP_CLASS;

    // `<td>`, never `<th>` — a `<th>` would pick up `.content th`'s
    // background/alignment rules (see the reset CSS in buildSelfContainedHtml).
    const thead = document.createElement('thead');
    const theadCell = document.createElement('td');
    const logoBand = document.createElement('div');
    logoBand.className = LOGO_BAND_CLASS;
    const img = document.createElement('img');
    img.src = logoDataUrl;
    img.alt = '';
    logoBand.appendChild(img);
    theadCell.appendChild(logoBand);
    thead.appendChild(document.createElement('tr')).appendChild(theadCell);

    // `.pdf-page-body` holder is MANDATORY, not cosmetic (spike defect 2):
    // `.content td > * { margin: 0; min-height: 20px }` would otherwise zero
    // every heading/blockquote margin directly under the `<td>` — the holder
    // div makes page blocks GRANDCHILDREN of the cell instead, out of that
    // selector's reach.
    const tbody = document.createElement('tbody');
    const tbodyCell = document.createElement('td');
    const pageBody = document.createElement('div');
    pageBody.className = PAGE_BODY_CLASS;
    pageChildren.forEach((node) => pageBody.appendChild(node)); // moves the node out of `clone`
    tbodyCell.appendChild(pageBody);
    tbody.appendChild(document.createElement('tr')).appendChild(tbodyCell);

    wrapper.append(thead, tbody);
    return wrapper;
}

/**
 * Replaces `clone`'s top-level children with one `<table class="pdf-page-wrap">`
 * per model page, logo repeated in each wrapper's `<thead>` — see this
 * section's module comment. Mutates `clone` in place (mirrors
 * `splitClonedTablesAtRows` / `splitClonedParagraphsAtLines`); a no-op when
 * `clone` has no children.
 *
 * Marker transfer (step 4) is scoped to `clone`'s OWN top-level members only
 * — `PAGE_BREAK_ATTR` is stripped from every node in the original snapshot
 * (never recursed into descendants) and re-applied to wrapper elements
 * `index > 0` only. This is NOT a recursive sweep: a marker on a
 * GRANDCHILD (e.g. the continuation box `splitOneTableAtRows` inserts inside
 * a `.tableScroll` wrapper, or a split paragraph continuation nested inside a
 * composite block) is a legitimate in-cell forced break — Chromium still
 * honours it (measured, Candidate B in the plan's §1), the wrapper table
 * fragments there, and its `<thead>` repeats, so that page is still topped by
 * the logo. Stripping it here would be both unnecessary and wrong: it isn't a
 * former direct child of `clone`, so this function never touches it.
 */
export function wrapClonePagesWithLogoHeader(clone: HTMLElement, logoDataUrl: string): void {
    const children = Array.from(clone.childNodes);
    if (children.length === 0) return;

    const runs = groupClonePagesForLogoHeader(children);

    // Strip PAGE_BREAK_ATTR from every captured top-level node BEFORE moving
    // any of them — the wrapper itself (not its former children) is what
    // carries the marker onward (see the wrapper loop below).
    children.forEach((node) => {
        if (node instanceof HTMLElement) node.removeAttribute(PAGE_BREAK_ATTR);
    });

    // Empty-run guard (step 5): a run with zero members (see
    // groupClonePagesForLogoHeader's own doc comment for when this occurs)
    // must never produce a wrapper — a marker at child index 0 would
    // otherwise become a logo-only blank page (A-29).
    const nonEmptyRuns = runs.filter((run) => run.length > 0);

    const wrappers = nonEmptyRuns.map((run, index) => {
        const wrapper = buildLogoPageWrapper(run, logoDataUrl);
        if (index > 0) wrapper.setAttribute(PAGE_BREAK_ATTR, '');
        return wrapper;
    });

    // Every original child was moved into some wrapper's `.pdf-page-body`
    // above (an empty run, by construction, held none to move) — `clone` is
    // now empty. Append the wrappers back in run order.
    wrappers.forEach((wrapper) => clone.appendChild(wrapper));
}

// ── HTML payload builder ──────────────────────────────────────────────────────

/**
 * Parses a CSS px length (e.g. a `getComputedStyle(...).paddingLeft` read,
 * `"96px"`) and returns its NEGATION as a px string (`"-96px"`) — used to
 * cancel a measured padding via `margin-left` without touching the padding
 * itself (see the `.${LOGO_BAND_CLASS}` rule inside `buildSelfContainedHtml`:
 * moving the padding would change every line's wrap width and invalidate the
 * page-break model's own measurement, which is forbidden). Unparsable input
 * (empty string, non-numeric — e.g. jsdom's `getComputedStyle`, which
 * resolves no stylesheet rules in tests) falls back to `'0px'`: no offset,
 * i.e. today's position, rather than guessing. Exported so this pure parse
 * is directly unit-testable, isolated from `buildSelfContainedHtml`'s
 * DOM/network side (mirrors the `collectUsedFontFamilies` precedent above).
 */
export function negatePxLength(raw: string): string {
    const px = parseFloat(raw);
    return Number.isFinite(px) ? `${-px}px` : '0px';
}

export async function buildSelfContainedHtml(
    contentEl: HTMLElement,
    filename: string,
    injectModelPageBreaks: boolean,
    pnoLogoDataUrl?: string,
    capacityScale = 1,
): Promise<string> {
    // RECUR-27604: the model reserves the logo-header band's own height ONLY
    // when a logo is actually present — absent, `reservedPageTopPx` is 0 and
    // every downstream call (cloneContentForExport -> computePageStartUnits)
    // is byte-identical to before this rework (A-14/A-20).
    const reservedPageTopPx = pnoLogoDataUrl ? PNO_HEADER_BAND_PX : 0;
    // RECUR-27604 corrective: how far the band must step left to cancel the
    // export root's own padding-left (PlateEditor.module.scss `.content`) so
    // it hugs the page's physical content-box edge instead of the content's
    // indent — read from the LIVE root BEFORE cloning (a detached clone has
    // no layout of its own to measure). No logo, nothing to offset — stays
    // the inert '0px' default, same gating as `reservedPageTopPx` above.
    const logoLeftOffset = pnoLogoDataUrl ? negatePxLength(getComputedStyle(contentEl).paddingLeft) : '0px';
    // RECUR-27604 variant C ("raised top"): the logo branch alone reduces the
    // @page TOP margin from the calibrated 0.6in to 0.35in (left/right/bottom
    // stay 0.6in) — this file's own `padding-top: 0` on the export root
    // (above, in logoCss) already keeps the band's y IDENTICAL across every
    // page; this additionally raises where that identical y sits, closer to
    // the sheet's physical edge. Direction-of-safety: `getServerPageHeightPx`
    // was calibrated against a real render at 0.6in top+bottom margins, so the
    // freed 0.25in (~29 content px at the One Pager's 1229px width — 0.25in x
    // 72pt/in = 18pt physical, divided by the ~0.6147pt/px fit-to-width scale)
    // is extra physical capacity this branch unlocks. In the logo branch
    // (`reservedPageTopPx > 0`) the model RECLAIMS exactly that much back via
    // `getLogoPageTopReclaimPx` (pageStartUnits.ts `computePageStartIndices`),
    // so its per-page capacity now MATCHES the physical page rather than
    // staying conservative — see `SERVER_LOGO_PAGE_TOP_RECLAIM_PT` in
    // pageGeometry.ts for why the reclaim is pinned to this exact margin
    // delta. No logo: unchanged uniform 0.6in on every side, no reclaim —
    // byte-identical to before this rework (A-14/A-27).
    const pageMargin = pnoLogoDataUrl ? '0.35in 0.6in 0.6in' : '0.6in';
    // Clone and pre-process via the shared exportHelpers pipeline.
    const { clone, rowPageStarts, linePageStarts, rowSegmentPageStarts } = cloneContentForExport(
        contentEl,
        injectModelPageBreaks,
        reservedPageTopPx,
        capacityScale,
    );
    // RECUR-27304 Delta v5: split paragraphs at their model-decided LINE
    // starts FIRST, before any other clone-mutating step. `findSplitPoint`
    // measures caret positions against the LIVE `contentEl` (stable
    // throughout this whole pipeline — only the CLONE is ever mutated), but
    // RESOLVING that position in the clone depends on the clone's own
    // descendant structure still matching the live block's 1:1 —
    // `trimTrailingWhitespace` specifically can prune/trim a trailing text
    // node INSIDE whichever block ends the document (see its own doc
    // comment), which would shift child-node indices out from under an
    // already-recorded path. Running this before that (and before
    // stripInteractiveChrome / trimTrailingEmptyBlocks /
    // normalizeTableColumnsForPrint — none of which mutate a plain
    // paragraph's OWN descendants either, but there's no reason to risk it)
    // removes the window entirely. Table row-splitting is unaffected either
    // way — it pairs clone<->live tables via `querySelectorAll('table')`
    // (see splitClonedTablesAtRows), which doesn't care how many extra `<p>`
    // siblings paragraph-splitting adds elsewhere in the document; the
    // reverse order would NOT be safe (see splitClonedParagraphsAtLines's doc
    // comment) since table-splitting inserts new top-level siblings that
    // would shift the top-level BLOCK INDEX paragraph-splitting pairs on.
    if (linePageStarts.length > 0) {
        splitClonedParagraphsAtLines(clone, contentEl, linePageStarts);
    }
    stripInteractiveChrome(clone);
    trimTrailingEmptyBlocks(clone);
    trimTrailingWhitespace(clone);
    // Pass the live content element so column widths are MEASURED from the
    // rendered layout (tables pair with the clone's by document order) — the
    // colgroup alone can't reproduce what the user sees for partially-sized
    // tables (see normalizeTableColumnsForPrint).
    normalizeTableColumnsForPrint(clone, contentEl);
    // AFTER normalize, so the clone<->live table pairing it relies on is still
    // 1:1 and every continuation copies the FINALIZED colgroup (Delta v3).
    // RECUR-27604 §14: a 'rowSegment' start must ALSO reach this step —
    // without `rowSegmentPageStarts` in the gate, a mid-row cut plan is
    // silently dropped and the giant row exports atomic (editor/PDF count
    // desync).
    if (rowPageStarts.length > 0 || rowSegmentPageStarts.length > 0) {
        splitClonedTablesAtRows(clone, contentEl, rowPageStarts, rowSegmentPageStarts);
    }
    // Mark the editor root so the wrapper can strip ONLY its editor card chrome
    // (border / radius / shadow / outline) and editing min-height. Scoped to the
    // root so tables, cells and everything inside are left exactly as the app's
    // own CSS renders them — DO NOT add table/cell/`body > *` rules here.
    clone.setAttribute('data-pdf-export-root', '');

    // RECUR-27304: pin the export root to the SAME width the page-break MODEL
    // measured (`computePageStartUnits(contentEl)` reads `contentEl.offsetWidth`
    // — see pageStartUnits.ts) — but ONLY when `injectModelPageBreaks` is on.
    // The pin exists so the render width equals the width the page-break MODEL
    // measured; for every other export surface (Engagement Letter, NDA,
    // BUREAU_KYC_CONSENT, APPLICATION_FORM via PlateDocumentDrawer, DocxDrawer,
    // compose-email attachments) no model ever ran, so there is nothing for the
    // width to stay consistent WITH — pinning there would just be an
    // uncommanded visual change to surfaces this fix doesn't own. Those keep
    // their historical unpinned rendering. Without this pin on the page-gap
    // surfaces, the clone serialises into <body> with no width pinned, so the
    // headless-Chromium Lambda renders it at the NARROW A3 page width instead
    // of the wide live-editor width the model measured against.
    // `normalizeTableColumnsForPrint` sizes columns as percentages, so the
    // narrower render wraps cell text onto MORE lines than the model assumed —
    // every wrapped row renders taller, the PDF fits far fewer rows per page
    // than predicted, and Chromium inserts extra breaks the in-editor overlay
    // never showed. Read from `contentEl` (the LIVE element), NOT the clone —
    // it must equal what the model measured; the clone is detached and has no
    // layout of its own. Inline `!important` beats the app's own `width: 100%`
    // rule regardless of specificity/order — an attribute selector added to
    // the `[data-pdf-export-root]{...}` block below could still tie with that
    // class rule. Guarded exactly like the model guards its own width (finite
    // and > 0) so the two can never disagree: when the width isn't usable the
    // model also returns no page-starts, so nothing would be injected anyway.
    // Verified against the real dev Lambda: it SCALES wider-than-page content
    // down to fit rather than clipping it, so pinning to the editor's (wider)
    // width is safe.
    if (injectModelPageBreaks) {
        const contentWidthPx = contentEl.offsetWidth;
        if (Number.isFinite(contentWidthPx) && contentWidthPx > 0) {
            clone.style.setProperty('width', `${contentWidthPx}px`, 'important');
            clone.style.setProperty('box-sizing', 'border-box', 'important');
        }
        // onepager-pdf-orphan-blank-page: strip the editor root's own padding-top
        // (editor-card chrome — PlateEditor.module.scss `.content`, ~72px) for the
        // model-driven export. The page-break MODEL measures the LIVE editor WITH
        // this padding, so page 1's first block sits at offsetTop === paddingTop
        // (not 0); pageBreakModel then anchors page 1 to that non-zero top and
        // hands page 1 ~72px of extra modeled capacity (its comment assumes a
        // non-logo doc's first block is at 0 — false whenever the root has this
        // padding). The logo branch already strips it (`logoCss` padding-top: 0);
        // the no-logo branch used to KEEP it, so a brim-full page 1 packed one row
        // past what the physical page holds and Chromium overflowed it — orphaning
        // that row onto an almost-blank next page. Stripping it here shifts page-1
        // content flush to the content-box top exactly like the logo branch, so
        // page 1's modeled capacity is conservative (breaks EARLIER than physical,
        // never overflows). Only page 1 carries root padding-top, so no other page
        // moves; non-model surfaces (EL/NDA/etc.) never enter this branch and keep
        // their historical rendering.
        clone.style.setProperty('padding-top', '0', 'important');
    }

    // Embed every image as a data: URI BEFORE serialising. ImageElement renders a
    // docId as a presigned https:// S3 URL; the Lambda aborts all non-data: network
    // fetches during render, so an un-inlined <img> renders blank. Done on the
    // detached clone so the live editor is untouched.
    await inlineImagesToDataUris(clone);

    // Pacifico (the EL signature font) is now self-hosted same-origin in index.scss,
    // so inlineFonts() reads its @font-face and embeds the bytes alongside Gilroy —
    // no special-case Google fetch needed.
    const [hostStyles, fontStyles] = await Promise.all([collectFullCss(), inlineFonts(clone)]);

    // RECUR-27604: wraps every model page into its own `<table class="pdf-page-wrap">`
    // with the logo in the `<thead>` — dead LAST among clone mutations, right
    // before serialising. Must run AFTER `inlineImagesToDataUris` (the logo's
    // own `src` is already a data: URI, so it must not enter that pass) and
    // after `collectFullCss`/`inlineFonts` above (neither reads the wrapper —
    // `inlineFonts` only scans inline `font-family` styles, which the wrapper
    // sets none of) so this placement is safe. Ordering is otherwise
    // load-bearing: `normalizeTableColumnsForPrint` and `splitClonedTablesAtRows`
    // (both already run, above) pair live<->clone tables by
    // `querySelectorAll('table')` document order — a wrapper table inserted
    // before either would corrupt that pairing — and `trimTrailingEmptyBlocks`/
    // `trimTrailingWhitespace` (also already run) walk trailing TOP-LEVEL
    // children, which the wrapper would otherwise hide a level down.
    if (pnoLogoDataUrl) {
        wrapClonePagesWithLogoHeader(clone, pnoLogoDataUrl);
    }
    const bodyContent = clone.outerHTML;
    const safeTitle = filename.replace(/[<>&"]/g, '');

    // Optional PNO page header (RECUR-27604): per-model-page wrapper table,
    // logo in the <thead>, which Chromium repeats on any page the wrapper
    // fragments onto — so a drift-created page is still topped by the logo
    // and the band can never land mid-page. Emitted ONLY when a logo data URI
    // is supplied — an unconfigured PNO (or a failed logo fetch) leaves the
    // no-logo document byte-identical to before this rework (A-14/A-27).
    // Every declaration below is spike-measured: see the plan's §1.5 for the
    // four defects these neutralise. The three `in` values are interpolated
    // from pageGeometry.ts's constants, never re-typed.
    const logoCss = pnoLogoDataUrl
        ? `[data-pdf-export-root] { padding-top: 0 !important; padding-bottom: 0 !important; }
      /* padding-bottom: 0 — defect 4: no blank trailing page.
         padding-top: 0 — RECUR-27604 corrective: page 1's wrapper now starts
         flush with the content-box top, exactly like every other page, so
         the band's y is identical on EVERY page (previously ~72px lower on
         page 1 only — this root's own padding-top used to render ABOVE the
         first wrapper). The page-break MODEL is deliberately left measuring
         WITH this padding still present (pageGeometry.ts / PNO_HEADER_BAND_PX
         was calibrated against the live editor, padding included) — so page
         1's modeled capacity is now conservative by that same padding:
         content can only break EARLIER than physically necessary on page 1,
         never overflow. */
      [data-pdf-export-root] > table.${PAGE_WRAP_CLASS} {
        width: 100% !important; max-width: 100% !important; table-layout: fixed !important;
        border-collapse: collapse !important; border-spacing: 0 !important;
        margin: 0 !important; padding: 0 !important; border: none !important;
        background: transparent !important; font: inherit !important;
        break-inside: auto !important; page-break-inside: auto !important;
      }
      /* MUST stay avoid — \`break-inside: auto\` on a <thead> makes Chromium treat the
         header group as non-repeatable and the band silently vanishes from every
         continuation page (spike defect 1). */
      [data-pdf-export-root] > table.${PAGE_WRAP_CLASS} > thead {
        break-inside: avoid !important; page-break-inside: avoid !important;
      }
      [data-pdf-export-root] > table.${PAGE_WRAP_CLASS} > tbody,
      [data-pdf-export-root] > table.${PAGE_WRAP_CLASS} > tbody > tr {
        break-inside: auto !important; page-break-inside: auto !important;
      }
      [data-pdf-export-root] > table.${PAGE_WRAP_CLASS} > thead > tr,
      [data-pdf-export-root] > table.${PAGE_WRAP_CLASS} > tbody > tr {
        background: transparent !important; border: none !important;
      }
      [data-pdf-export-root] > table.${PAGE_WRAP_CLASS} > thead > tr > td,
      [data-pdf-export-root] > table.${PAGE_WRAP_CLASS} > tbody > tr > td {
        height: auto !important; min-height: 0 !important; padding: 0 !important;
        border: none !important; background: transparent !important;
        vertical-align: top !important; line-height: inherit !important;
        position: static !important; word-break: inherit !important;
        overflow-wrap: inherit !important; text-align: left !important;
      }
      [data-pdf-export-root] > table.${PAGE_WRAP_CLASS} > thead > tr > td > *,
      [data-pdf-export-root] > table.${PAGE_WRAP_CLASS} > tbody > tr > td > * {
        margin: 0 !important; min-height: 0 !important;
      }
      /* RECUR-27604 corrective: cancel the export root's own padding-left
         (PlateEditor.module.scss \`.content\`, currently 96px) via a negative
         margin so the band's left edge sits at the page's physical
         content-box edge instead of the content's indent — moving the
         padding itself was rejected: it would change every line's wrap width
         and invalidate the page-break model's own measurement. Read from the
         LIVE root's computed style (see buildSelfContainedHtml, measured
         BEFORE cloning) rather than hardcoded, so this tracks that rule if
         it ever changes; falls back to 0px (today's position) when the
         measurement can't be trusted (e.g. jsdom in tests — see
         negatePxLength). Safe to overhang past the box: no ancestor in this
         wrapper chain (table.${PAGE_WRAP_CLASS} / thead / tr / td, all reset
         above) sets \`overflow\`, and this div's OWN \`overflow: hidden\` only
         clips its own content — the box (and its clip region) moves with the
         margin, nothing ancestral clips the shift.
         MUST be scoped under this exact ancestor chain, not the bare
         one-class \`.${LOGO_BAND_CLASS}\` selector below: the reset rule
         directly above (\`… > td > *\`) already matches this SAME element
         (it IS that \`<td>\`'s child) with an \`!important\` margin reset,
         at higher specificity (two classes/attributes there vs one on a
         bare class selector) — that reset wins the fight regardless of
         \`!important\` on both sides, since specificity is compared before
         source order ever matters. Scoping this rule under the identical
         ancestor chain gives it THREE classes/attributes, one more than the
         reset it must beat, so it wins outright (render-verified: the band
         never actually moved in a real Chrome render until this was scoped
         this way). */
      [data-pdf-export-root] > table.${PAGE_WRAP_CLASS} > thead > tr > td > .${LOGO_BAND_CLASS} {
        margin-left: ${logoLeftOffset} !important;
      }
      /* RECUR-27604 variant C ("wide letterhead"): a FIXED-SIZE band box
         (width-led image inside it), not a height-led image with a max-width
         cap — any logo aspect renders aspect-true inside the box: a wide
         lockup fills the width, a tall/square mark caps at the box height,
         anchored left via object-position (never centered/clipped). */
      .${LOGO_BAND_CLASS} {
        height: ${PNO_LOGO_MAX_HEIGHT_IN}in; padding-bottom: ${PNO_LOGO_GAP_BELOW_IN}in; box-sizing: content-box;
        overflow: hidden;
      }
      .${LOGO_BAND_CLASS} img {
        width: ${PNO_LOGO_WIDTH_IN}in; height: ${PNO_LOGO_MAX_HEIGHT_IN}in;
        object-fit: contain; object-position: left center; display: block;
      }`
        : '';

    // Two targeted, layout-neutral print corrections only:
    //   1. [data-pdf-export-root] — remove the editor's card border + editing
    //      min-height (the stray top/left border in the PDF).
    //   2. @page box + print colour fidelity.
    // The blank trailing page is handled in the DOM by trimTrailingWhitespace.
    return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${safeTitle}</title>
    ${hostStyles}
    ${fontStyles}
    <style>
      @page { size: A3 portrait; margin: ${pageMargin}; }
      html, body { margin: 0; padding: 0; background: #ffffff; }
      [data-pdf-export-root] { border: none !important; border-radius: 0 !important; box-shadow: none !important; outline: none !important; min-height: 0 !important; }
      /* normalizeTableColumnsForPrint() already made every table fit its
         container (% columns + the app's width:100%), so the live editor's
         horizontal scroll box (.tableScroll, RECUR-26175) has no overflow here.
         Neutralise it anyway so the static print HTML can never render a clipped
         table or a scrollbar. Layout-neutral — not a table/cell restyle. */
      [class*="tableScroll"] { overflow: visible !important; }
      /* The full host CSS we ship carries TWO legacy GLOBAL rules,
         "tr { page-break-inside: avoid }" AND "p { page-break-inside: avoid }"
         (TextEditor's TableSelector.module.scss — bare element selectors, which SCSS
         does NOT scope inside a CSS module, so they leak app-wide). They pin every
         table row and every paragraph, so one taller than the space left on the page
         is pushed WHOLE to the next page — leaving a big gap — instead of flowing
         across the break. Force auto on the export's own tables and paragraphs so
         they paginate continuously (RECUR-26249). */
      [data-pdf-export-root] table, [data-pdf-export-root] tbody, [data-pdf-export-root] tr, [data-pdf-export-root] td, [data-pdf-export-root] th, [data-pdf-export-root] p { break-inside: auto !important; page-break-inside: auto !important; }
      *, *::before, *::after { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
      /* Deterministic export (RECUR-27304 Delta v3, injectModelPageBreaks). The
         marker only ever sits on a BLOCK-LEVEL box — a whole-block page-start
         element, or a split-out continuation <table>/wrapper (see
         splitClonedTablesAtRows) — which Chromium honours reliably, unlike an
         unverified <tr> break-before dependency (deliberately not used — see
         the plan's Delta v3 "Tables" section). The marker is applied ONLY to
         the detached CLONE (see cloneContentForExport) — the live editor DOM
         is never mutated, so it only ever exists in this detached HTML. */
      [data-pdf-export-root] [${PAGE_BREAK_ATTR}] { break-before: page !important; page-break-before: always !important; }
      /* Belt-and-braces kill-switch (RECUR-27304): the clone is already
         stripped of the live gap-display styling (see stripPageGapStyling in
         cloneContentForExport above), but zero the page-gap variable here too
         so any FUTURE editor-only gap styling can never leak into the export
         — the PDF's page gaps must come ONLY from its own forced breaks
         above; leaking the editor's visual margin on top of them
         double-counts the gap into stranded headings and near-empty pages. */
      [data-pdf-export-root], [data-pdf-export-root] * { --page-gap: 0px !important; --page-end-gap: 0px !important; }
      ${logoCss}
    </style>
  </head>
  <body>${bodyContent}</body>
</html>`;
}

// ── aica fetch ─────────────────────────────────────────────────────────────────

// The render Lambda answers HTTP 200 even when it can't produce a PDF (e.g. a
// >6MB synchronous request) — the response body is a small JSON error
// envelope ({"responseData":{"responseCode":40,"responseMessage":"..."}}, a
// couple hundred bytes) instead of binary PDF bytes. `responseType: 'blob'`
// never inspects the body, so without this check that JSON envelope silently
// becomes the "PDF" wrapped downstream and handed to the user — the classic
// silent failure this ticket is about. Detected via a size floor (a real
// rendered PDF is always far bigger than any JSON error body) plus the `%PDF`
// magic-number check, and thrown so the export fails loudly (Sentry capture +
// the caller's failure toast) instead of shipping that envelope as a "PDF".
const MIN_PLAUSIBLE_PDF_BYTES = 2048; // the Lambda's JSON error envelope is ~197 bytes.
const PDF_MAGIC_BYTES = '%PDF';

async function assertPdfResponse(blob: Blob): Promise<void> {
    const header = await blob.slice(0, PDF_MAGIC_BYTES.length).text();
    if (blob.size >= MIN_PLAUSIBLE_PDF_BYTES && header === PDF_MAGIC_BYTES) return;

    const snippet = await blob.text().catch(() => '<unreadable response body>');
    console.error('[exportEditorToPdfServer] render service returned an error instead of a PDF:', snippet.slice(0, 500));
    throw new Error(`render service returned a non-PDF response (${blob.size} bytes): ${snippet.slice(0, 500)}`);
}

// Exported (alongside the file's existing cloneContentForExport /
// splitClonedTablesAtRows precedent) so the non-PDF-response detection is
// directly unit-testable without the gzip pipeline — jsdom has neither
// CompressionStream nor Blob.prototype.stream, an environment gap unrelated
// to this fix.
export async function fetchPdfFromAica(htmlGz: string, fileName: string): Promise<Blob> {
    const response = await axios({
        baseURL: aicaBaseUrl,
        url: '/document/pdf/render',
        method: 'POST',
        data: { htmlGz, fileName },
        responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    await assertPdfResponse(blob);
    return blob;
}

function triggerDownload(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename.endsWith('.pdf') ? filename : `${filename}.pdf`;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Revoke after a short delay so the download has time to start.
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

// ── Self-correcting capacity retry (RECUR-27726) ──────────────────────────────
//
// `getServerPageHeightPx` (pageGeometry.ts) is an EMPIRICALLY-measured
// capacity that can still overestimate Chromium's real usable page height at
// a given content width — when it does, the model plans fewer pages than
// Chromium's own native fragmentation produces, and the forced break
// (`data-pdf-page-break`) the model injected for its NEXT planned page lands
// mid-Chromium-page, stranding a near-empty page behind it. Retried ONCE with
// a tightened per-export capacity multiplier rather than mutating the shared
// `SERVER_CONTENT_ASPECT`/`SERVER_PAGE_SAFETY_MARGIN` constants, which the
// always-mounted overlay also reads and must keep its own (looser) tolerance.

const MIN_CAPACITY_SCALE = 0.8;
const MAX_CAPACITY_SCALE = 0.97;

/**
 * The retry's tightened page-capacity multiplier: how much smaller the
 * planned page count was than the actual rendered count, clamped so one
 * wildly-off export can't collapse capacity to near zero (`MIN_CAPACITY_SCALE`)
 * or leave the retry effectively unscaled (`MAX_CAPACITY_SCALE`).
 */
export function clampCapacityScale(plannedPages: number, actualPages: number): number {
    return Math.min(MAX_CAPACITY_SCALE, Math.max(MIN_CAPACITY_SCALE, plannedPages / actualPages));
}

export interface PdfRenderRetryResult extends PdfRenderRetryContext {
    blob: Blob;
    /**
     * Page count already measured for `blob`, when known — this function
     * decodes `firstBlob` once to decide whether to retry, so `attempt: 1`
     * always carries that count and the caller's desync report can reuse it
     * instead of decoding the SAME bytes again. `attempt: 2` means `blob` is
     * a freshly-rendered retry that has never been measured, so this stays
     * `undefined` — the caller decodes it once, for real.
     */
    actualPages?: number | null;
}

/**
 * Given the FIRST successful render's blob, decides whether the page-break
 * model's plan undershot Chromium's actual rendered page count and, if so,
 * retries ONCE via `retryRender` at a tightened capacity (`clampCapacityScale`).
 * `retryRender` is injected so this decision is testable without the gzip/axios
 * pipeline (jsdom has neither `CompressionStream` nor `Blob.prototype.stream`).
 *
 * At most one retry: a retry that THROWS keeps the first blob (a desynced PDF
 * beats no PDF) — the caller's `reportPageCountDesyncIfAny` will still flag
 * the first blob's desync. A retry that still desyncs keeps the SECOND blob
 * regardless (a tighter capacity is strictly closer to correct) and lets that
 * same report flag it too, tagged with `attempt: 2`.
 */
export async function retryPdfRenderOnDesync(
    firstBlob: Blob,
    plan: ExportPlanSnapshot | null,
    retryRender: (capacityScale: number) => Promise<Blob>,
): Promise<PdfRenderRetryResult> {
    if (!plan) return { blob: firstBlob, attempt: 1 };

    const actualPages = await estimatePdfPageCount(firstBlob);
    if (actualPages == null || actualPages <= plan.plannedPages) return { blob: firstBlob, attempt: 1, actualPages };

    const capacityScale = clampCapacityScale(plan.plannedPages, actualPages);
    addPdfExportBreadcrumb('pdf page-count desync detected — retrying render with tightened capacity', {
        plannedPages: plan.plannedPages,
        actualPages,
        capacityScale,
    });

    try {
        const retryBlob = await retryRender(capacityScale);
        // retryBlob is a fresh render — nothing has decoded it yet, so there's
        // no earlier count to hand back here (see actualPages' own doc comment).
        return { blob: retryBlob, attempt: 2, capacityScale };
    } catch (retryErr) {
        addPdfExportBreadcrumb('pdf export retry failed — keeping the first render', {
            error: retryErr instanceof Error ? retryErr.message : String(retryErr),
        });
        return { blob: firstBlob, attempt: 1, actualPages };
    }
}

/**
 * Which plan the post-render desync check should compare against. A
 * SUCCESSFUL retry (`attempt: 2`) re-rendered at a tightened `capacityScale`,
 * so the original (unscaled) plan always undercounts relative to it — a
 * correct retry (2-page plan → a genuinely-3-page result at the tighter
 * capacity) would otherwise report a false desync every time. Re-derives the
 * plan at the retry's own capacity + the SAME reservation so the comparison
 * is against what actually produced the final blob; falls back to
 * `originalPlan` when there was no retry, the retry failed (`attempt: 1`), or
 * the re-derive itself fails.
 */
export function resolvePlanForDesyncCheck(
    contentEl: HTMLElement,
    reservedPageTopPx: number,
    originalPlan: ExportPlanSnapshot | null,
    retryResult: PdfRenderRetryContext,
): ExportPlanSnapshot | null {
    if (retryResult.attempt !== 2 || retryResult.capacityScale == null) return originalPlan;
    return snapshotExportPlan(contentEl, reservedPageTopPx, retryResult.capacityScale) ?? originalPlan;
}

/** Builds and renders one PDF via aica — shared by the first render and the desync retry. */
async function renderServerPdf(
    contentEl: HTMLElement,
    filename: string,
    injectModelPageBreaks: boolean,
    pnoLogoDataUrl?: string,
    capacityScale = 1,
): Promise<Blob> {
    const html = await buildSelfContainedHtml(contentEl, filename, injectModelPageBreaks, pnoLogoDataUrl, capacityScale);
    const htmlGz = await gzipToBase64(html);
    addPdfExportBreadcrumb('html built', { htmlBytes: html.length, gzBytes: htmlGz.length, capacityScale });
    return fetchPdfFromAica(htmlGz, filename);
}

// ── Public API ────────────────────────────────────────────────────────────────

export async function exportEditorToPdfServer({
    target,
    filename = 'document',
    returnBlob = false,
    injectModelPageBreaks = false,
    pnoLogoDataUrl,
}: ExportToPdfServerOptions): Promise<Blob | void> {
    // Locate the editor content root.
    const contentEl =
        (target.querySelector('[data-testid="plate-editor-content"]') as HTMLElement | null) ??
        (target.querySelector('[contenteditable]') as HTMLElement | null) ??
        target;

    // Wait for images before serialising so src attributes are fully resolved.
    await waitForImages(contentEl);

    // Sentry (paginationTelemetry): the whole pipeline runs inside one span,
    // the model's plan is snapshotted for the desync check below, and a
    // server failure is CAPTURED and rethrown (no raster fallback —
    // RECUR-26973; the raster path also knows nothing about the RECUR-27604
    // logo band, so falling back would silently ship a logo-less PDF).
    // Telemetry never throws and no-ops entirely outside prod (Sentry.init
    // is prod-only).
    return Sentry.startSpan(
        {
            name: 'plate.pdf.export.server',
            op: 'plate.pdf.export',
            attributes: { 'plate.inject_model_page_breaks': injectModelPageBreaks },
        },
        async (): Promise<Blob | void> => {
            // Snapshot AFTER waitForImages so the plan measures the same
            // stable layout buildSelfContainedHtml is about to measure —
            // with the SAME logo-band capacity reservation the export
            // itself will use (RECUR-27604), so plannedPages can't desync
            // from the render by construction.
            const reservedPageTopPx = pnoLogoDataUrl ? PNO_HEADER_BAND_PX : 0;
            const plan = injectModelPageBreaks ? snapshotExportPlan(contentEl, reservedPageTopPx) : null;

            let blob: Blob;
            try {
                blob = await renderServerPdf(contentEl, filename, injectModelPageBreaks, pnoLogoDataUrl);
            } catch (err) {
                // NO raster fallback (RECUR-26973): the raster path produced
                // PDFs whose page boundaries diverged from the previewed
                // model and emitted non-standard page sizes, so a silent
                // fallback traded an honest error for a wrong document.
                // Capture for Sentry, then rethrow — every caller
                // (PreviewButton, PlateDocumentDrawer, HiddenPdfExporter)
                // already catches and shows its own failure toast.
                reportPdfServerExportFailure(err, plan);
                const e = err as { response?: { status?: number }; code?: string; message?: string };
                console.error(
                    '[exportEditorToPdfServer] aica PDF request failed:',
                    e?.response?.status ?? e?.code ?? e?.message ?? 'unknown error',
                );
                throw err;
            }

            // Self-correcting capacity retry (RECUR-27726): when the model's
            // plan undershot Chromium's actual rendered page count, retries
            // ONCE at a tightened capacity — see retryPdfRenderOnDesync's own
            // doc comment. A null plan (non-model exports) or an unparseable
            // PDF is zero extra network cost — the byte scan inside is a
            // cheap regex over the blob, not a render.
            const retryResult = await retryPdfRenderOnDesync(blob, plan, (capacityScale) =>
                renderServerPdf(contentEl, filename, injectModelPageBreaks, pnoLogoDataUrl, capacityScale),
            );
            blob = retryResult.blob;

            // Plan/render desync check (SEDNA bug class: editor plans N
            // pages, the PDF ships with more). Reports against whichever blob
            // is FINAL (post-retry, if any) — compared against the plan that
            // actually produced IT (see resolvePlanForDesyncCheck's own doc
            // comment for why a successful retry can't be compared against
            // the original plan). `retryResult.actualPages` reuses the count
            // `retryPdfRenderOnDesync` already decoded for THIS SAME blob
            // (the common no-retry case) instead of decoding it again —
            // `undefined` after an actual retry, since that blob is unmeasured.
            const finalPlan = resolvePlanForDesyncCheck(contentEl, reservedPageTopPx, plan, retryResult);
            // `triggerDownload` is synchronous, so kicking off the (still
            // async, up-to-25MB-chunked) desync report first and awaiting it
            // AFTER the download/return, rather than before, lets the user's
            // file leave immediately instead of waiting on best-effort
            // telemetry to settle.
            const desyncReport = reportPageCountDesyncIfAny(
                blob,
                finalPlan,
                { attempt: retryResult.attempt, capacityScale: retryResult.capacityScale },
                retryResult.actualPages,
            );

            if (returnBlob) {
                await desyncReport;
                return blob;
            }
            triggerDownload(blob, filename);
            await desyncReport;
        },
    );
}
